import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { fetchAiEventStrips } from '@/entities/strip';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, KEY_RECOIL, AUTO_EVENTS, DEVICE_TAB_TYPES } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectEmptySelectedKey,
  selectIsLoadingStrip,
  selectIsWaiting,
  useReportStore,
} from '@/entities/report';
import { MAP_AUTO_EVENT_TYPE } from '../constants';
import { downloadImageAndCache, formatStripByEvents } from '../handler';
import type { ManageStripsWrapperProps } from './index';

/**
 * Hosts the strips-management tab, loading its captured event strips and keeping them in sync with server-side updates. Reads the active event button, ECG data map, report info, waiting and header flags from the report store, fetches and formats the strips whenever the tab becomes active, and subscribes to the event-update, strip-reload, ECG-update and events-updated emitter channels to refetch, guarding the AI reloads on the strip loading flag and matching study id.
 *
 * @param props - Tab props carrying isActive, studyId, profileId and reportData.
 * @returns The tab view model with activeButton, ecgDataMap, timezoneOffset, emptySelectedKey, isWaiting and isShowTabHeader.
 */
const useManageStripsWrapper = ({
  reportData,
  isActive,
  studyId: studyIdProp,
  profileId: profileIdProp,
}: Pick<ManageStripsWrapperProps, 'reportData' | 'isActive' | 'studyId' | 'profileId'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Switches the strips-management tab's active event button in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New active event tab, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  /**
   * Reads the strips-management tab's currently active event button straight from the report store. Applies the tab-keyed active-tab selector to a fresh store snapshot so the value is current rather than captured at render time.
   *
   * @returns The active event tab, a rhythm, beat or beat-hr tab value.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyId, profileId, startReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const emptySelectedKey = useReportStore(selectEmptySelectedKey(keyRecoil));
  const isWaiting = useReportStore(selectIsWaiting(keyRecoil));
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const setEventStripsDataAction = useReportStore((s) => s.actions.setEventStripsData);
  /**
   * Stores the loaded event strips for the strips-management tab in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New event strip list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventStripsData = (v: Parameters<typeof setEventStripsDataAction>[1]) =>
    setEventStripsDataAction(keyRecoil, v);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Sets the strip loading flag for the strips-management tab in the report store. Forwards the value to the store action with the tab's record key already bound.
   *
   * @param v - New loading state, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);

  const isInitial = useRef(false);

  /**
   * Loads the study's captured event strips for the strips-management tab. Raises the loading flag, queries the AI event strips over the report's start and stop window, formats them against the facility time zone and study start, warms the strip image cache, stores them, and falls back to the all-auto-events tab when the active auto-event tab has no matching strips, logging and swallowing any fetch error.
   *
   * @returns A promise resolving once the strips are stored and the loading flag is cleared.
   */
  const fetchEvents = async () => {
    const activebtn = getActiveButton();
    const isAutoEvent =
      !DEVICE_TAB_TYPES.includes(activebtn as any) && (activebtn as any) !== 'ECG_DISCLOSURE';
    const { facility, start: startStudyDate } = reportData?.study || {};
    setIsLoadingStrip(true);
    try {
      const { timezone } = reportData?.study || {};
      const { start, stop } = reportData?.report || {};
      const eventStripsFilter = {
        studyId: studyIdProp,
        profileId: profileIdProp,
        startSearchDate: start,
        stopSearchDate: stop,
        isCaptured: true,
        sortOrder: 'asc',
      };
      const events = await fetchAiEventStrips(eventStripsFilter, 0, true);
      const eventStrips = formatStripByEvents(events, timezone, facility, startStudyDate);
      downloadImageAndCache(eventStrips);
      setEventStripsData(eventStrips);
      if (isAutoEvent) {
        const filterType = (MAP_AUTO_EVENT_TYPE as any)[activebtn];
        const filteredData = _.filter(eventStrips, (event) => _.includes(filterType, event.type));
        if (_.isEmpty(filteredData)) {
          setActiveButton(AUTO_EVENTS as any);
        }
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    } finally {
      setIsLoadingStrip(false);
    }
  };
  /**
   * Reloads the tab's event strips in response to an emitter notification. Starts the strip fetch without awaiting it.
   *
   * @returns Nothing.
   */
  const handleReloadEvents = () => {
    fetchEvents();
  };
  /**
   * Refetches the captured AI event strips after the server reports the AI results changed. Runs only once the tab has finished its initial load, clears the Apollo cache first so the strips come back fresh, then re-runs the strip fetch.
   *
   * @returns A promise resolving once the cache is cleared and the refetch has been started.
   */
  const handleReloadAiEvent = async () => {
    if (isInitial.current) {
      const config = {
        isReload: true,
        updatedType: { isUpdateAi: true },
      };
      await clearApolloCache();
      (fetchEvents as any)(config); // pre-existing bug: fetchEvents takes no params, extra arg silently dropped
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchEvents();
      isInitial.current = true;
    } else {
      isInitial.current = false;
    }
  }, [isActive]);
  useEmitter(EMITTER_CONSTANTS.UPDATE_EVENT_SUCCESS, handleReloadEvents, []);
  useEmitter(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP, handleReloadEvents, []);

  useEmitter(
    EMITTER_CONSTANTS.UPDATE_EVENT_ECG,
    (msg: any) => {
      if (!isLoadingStrip && reportData?.study?.id === msg?.study?._id) {
        handleReloadAiEvent();
      }
    },
    [isLoadingStrip, reportData?.study?.id],
  );

  useEmitter(
    EMITTER_CONSTANTS.EVENTSUPDATED_EVENT,
    () => {
      if (!isLoadingStrip) {
        handleReloadAiEvent();
      }
    },
    [isLoadingStrip],
  );

  return {
    activeButton,
    ecgDataMap,
    timezoneOffset,
    emptySelectedKey,
    isWaiting,
    isShowTabHeader,
  };
};

export default useManageStripsWrapper;
